/* 0.49.16 - The Progress bar element, after the Henaket progress bar:
 * a Blue 100 box 20px high with 4px corners and 2px 8px 2px 2px padding,
 * the bar and its label side by side 8px apart. The bar is a Blue 300
 * track with the Blue 800 fill over it, 2px corners (concentric with the
 * box's 4px). The label is semibold 16 / 16 Text 800, right-aligned in a
 * width that holds "100%", so the bar does not move as the value changes.
 *
 * Element fields (all optional):
 *   progress_value    the value: a number, {field} or =formula   ('')
 *   progress_min      the value that is 0%                       (0)
 *   progress_max      the value that is 100%                     (100)
 *   progress_label    'percent' (the value) | 'custom' | 'none' (percent)
 *   progress_percent  false: the value label shows the number itself
 *                     (13,000) instead of the percentage (65%)  (true)
 *   progress_text     the custom label: {percent} {value} {min} {max}
 *   progress_colors   'solid' | 'steps'                          (solid)
 *   progress_fill     the fill in solid mode   (Blue 800)
 *   progress_track    the empty part           (Blue 300)
 *   progress_box      0.49.62 - the frame around the bar, also behind
 *                     the label (Blue 100)
 *   progress_label_color  0.49.62 - the label's text      (Text 800)
 *   progress_steps    [{ from: 0, color }, { from: 50, color }, ...]: the
 *                     fill from that percentage up (steps mode)
 *   progress_animate  true: the bar slides to a new value
 *
 * The percentage is (value - min) / (max - min), kept between 0 and 100:
 * over max is 100%, under min is 0%. An empty or non-numeric value is 0%. */
import { colorCss } from '../lib/colorCss';
import { looseNumber } from '../lib/looseNumber';
import styles from './ProgressBar.module.css';

export const DEFAULT_PROGRESS_STEPS = [
  { from: 0, color: '--color-error' },
  { from: 50, color: '--color-warning' },
  { from: 100, color: '--color-success' },
];

const num = (v, dflt) => {
  if (v === undefined || v === null || v === '') return dflt;
  const n = looseNumber(typeof v === 'number' ? v : String(v));
  return n === null ? dflt : n;
};

/* The numbers and the percentage from resolved texts. */
export function progressNumbers(valueText, minText, maxText) {
  const min = num(minText, 0);
  const max = num(maxText, 100);
  const value = num(valueText, null);
  let percent = 0;
  if (value !== null && max !== min) percent = ((value - min) / (max - min)) * 100;
  if (!Number.isFinite(percent)) percent = 0;
  percent = Math.min(100, Math.max(0, percent));
  return { value, min, max, percent };
}

export function progressFillColor(e, percent) {
  if (e && e.progress_colors === 'steps') {
    const steps = (Array.isArray(e.progress_steps) && e.progress_steps.length ? e.progress_steps : DEFAULT_PROGRESS_STEPS)
      .filter(s => s && s.color !== undefined)
      .map(s => ({ from: Number(s.from) || 0, color: s.color }))
      .sort((a, b) => a.from - b.from);
    let pick = steps.length ? steps[0].color : '';
    for (const s of steps) if (percent >= s.from) pick = s.color;
    return colorCss(pick) || 'var(--color-blue-800)';
  }
  return colorCss(e && e.progress_fill) || 'var(--color-blue-800)';
}

export default function ProgressBar({ e = {}, numbers, label, ariaLabel }) {
  const { percent, value, min, max } = numbers;
  const shown = Math.round(percent);
  const mode = e.progress_label || 'percent';
  let text = '';
  // 0.49.17 - "Show in percent" off: the value as a number (formatted by
  // the caller, `label`), not the percentage.
  const asNumber = mode === 'percent' && e.progress_percent === false;
  if (mode === 'percent') text = asNumber ? (label != null ? label : (value === null ? '' : String(value))) : `${shown}%`;
  else if (mode === 'custom') text = label != null ? label : '';
  const track = colorCss(e.progress_track) || 'var(--color-blue-300)';
  const fill = progressFillColor(e, percent);
  const box = colorCss(e.progress_box);
  const labelColor = colorCss(e.progress_label_color);
  return (
    <div className={styles.box} style={box ? { background: box } : undefined}
         role="progressbar"
         aria-valuemin={min} aria-valuemax={max}
         aria-valuenow={value === null ? min : Math.min(max, Math.max(min, value))}
         aria-valuetext={text || `${shown}%`}
         aria-label={ariaLabel || undefined}>
      <div className={styles.track} style={{ background: track }}>
        <div className={`${styles.fill} ${e.progress_animate ? styles.animate : ''}`}
             style={{ width: `${percent}%`, background: fill }} />
      </div>
      {mode !== 'none' && (
        <span className={mode === 'percent' ? `${styles.label} ${styles.labelPercent}` : styles.label}
              style={labelColor ? { color: labelColor } : undefined}>{text}</span>
      )}
    </div>
  );
}
